import { configureStore } from '@reduxjs/toolkit';
import reducersObj from '@store/slices';
import { RootState } from '@utils/types';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  persistCombineReducers,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import type { PartnerSessionState } from './slices/partnerSession.slice';

export const sanitizePartnerSessionForPersistence = (
  inboundState: PartnerSessionState,
): PartnerSessionState => {
  if (!inboundState.partnerSession?.notes) {
    return inboundState;
  }

  const partnerSession = { ...inboundState.partnerSession };
  delete partnerSession.notes;

  return {
    ...inboundState,
    partnerSession,
  };
};

const partnerSessionTransform = createTransform<
  PartnerSessionState,
  PartnerSessionState
>(sanitizePartnerSessionForPersistence, (outboundState) => outboundState, {
  whitelist: ['partnerSession'],
});

const persistConfig = {
  key: 'root',
  storage,
  timeout: 5000,
  stateReconciler: autoMergeLevel2,
  whitelist: ['login', 'card', 'partnerSession'],
  transforms: [partnerSessionTransform],
  version: 1,
};

const persistedReducer = persistCombineReducers(persistConfig, reducersObj);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        /* Ignore persistance actions */
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    });
  },
});

export const persistor = persistStore(store);

export const useAppSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const dispatch = store.dispatch;
// export type AppDispatch = typeof dispatch;
